import React, { useMemo } from "react";
import { Button, Table, Tooltip } from "antd";
import { ColumnType } from "antd/lib/table";
import { InfoCircleOutlined } from "@ant-design/icons";
import { map, trim } from "lodash";
import CustomDot from "components/CustomDot";
import {
  AICC_ASSET_STATUS_COLOR,
  AICC_ASSET_STATUS_LABEL,
  AICC_ASSET_TYPE,
  AICC_ASSET_TYPE_LABEL,
  AICC_STATUS_DOT_SIZE,
  AiccAssetType,
} from "constants/aiccLanguage";
import { AiccLanguageAsset } from "types/model/aiccLanguage";
import { AiccTtsProviderVoiceResponse } from "types/dto/response/aiccLearningAssets";

import { ASSET_SCREEN_PATH, MANAGE_NO_SCREEN_HINT } from "../configs";
import styles from "./VersionRows.module.scss";

type VoicesById = Record<string, AiccTtsProviderVoiceResponse>;

interface Props {
  assets: AiccLanguageAsset[];
  note?: string;
  onManage?: (assetType: AiccAssetType) => void;
  voicesById: VoicesById;
}

// TTS lines arrive as "<slot>: <voice id>"; the name comes from the provider's voice list.
const withVoiceName = (line: string, voicesById: VoicesById) => {
  const name = voicesById[trim(line.split(":")[1])]?.name;
  return name ? `${line} (${name})` : line;
};

const getColumns = ({
  onManage,
  voicesById,
}: Pick<Props, "onManage" | "voicesById">): ColumnType<AiccLanguageAsset>[] => [
  {
    title: "Sub-Asset",
    dataIndex: "assetType",
    key: "assetType",
    // Fits the longest label, "Prompt: Feedback and Assessment".
    width: 280,
    render: (_, { assetType }) => AICC_ASSET_TYPE_LABEL[assetType],
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (_, { assetType, status, summary, detailLines }) => (
      <div>
        <div className={styles.assetStatus}>
          <CustomDot color={AICC_ASSET_STATUS_COLOR[status]} size={AICC_STATUS_DOT_SIZE}>
            {AICC_ASSET_STATUS_LABEL[status]}
          </CustomDot>
          {!!summary && <span className={styles.assetSummary}>{summary}</span>}
        </div>
        {map(detailLines, (line) => (
          <div key={line} className={styles.assetDetailLine}>
            {assetType === AICC_ASSET_TYPE.ttsSetting ? withVoiceName(line, voicesById) : line}
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    // One link wide now that Mark Preview-Ready is gone; the width it used to need went to Status.
    width: 140,
    render: (_, { assetType }) => {
      // Navigation, nothing more: it opens the sub-asset's screen on this version, and whether that
      // version can be written to is that screen's business. So a frozen copy is not a reason to
      // withhold it — only the screen not existing yet is.
      const canManage = !!ASSET_SCREEN_PATH[assetType] && !!onManage;

      return (
        <Tooltip title={canManage ? undefined : MANAGE_NO_SCREEN_HINT}>
          <span>
            <Button
              type="link"
              className={styles.linkButton}
              disabled={!canManage}
              onClick={() => onManage?.(assetType)}
            >
              Manage
            </Button>
          </span>
        </Tooltip>
      );
    },
  },
];

function AssetSubTable({ assets, note, onManage, voicesById }: Props) {
  const columns = useMemo(() => getColumns({ onManage, voicesById }), [onManage, voicesById]);

  return (
    <>
      {/*
        Only for the one state whose read-only rows have a way out that is not obvious: a
        withdrawn version. A published version's rows explain themselves by sitting under a row
        labelled Published, so they get no note.
      */}
      {!!note && (
        <div className={styles.assetsLocked}>
          <InfoCircleOutlined />
          {note}
        </div>
      )}

      <Table<AiccLanguageAsset>
        className={styles.assetTable}
        columns={columns}
        dataSource={assets}
        rowKey={(record) => record.assetType}
        pagination={false}
        size="small"
      />
    </>
  );
}

export default AssetSubTable;
